import React, { useEffect, useState } from "react";
import { CalendarRange, RefreshCcw } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * Admin-only forward-looking vacation planning report.
 * Fetches /api/vacations/quota-report and renders a sortable table:
 *   name · role · quota · used · pending · remaining · upcoming (next N days)
 */
export default function VacationQuotaReport() {
  const [rows, setRows] = useState([]);
  const [days, setDays] = useState(90);
  const [year, setYear] = useState(null);
  const [deadlines, setDeadlines] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vacations/quota-report", { params: { days } });
      setRows(data.rows || []); setYear(data.year); setDeadlines(data.planning_deadlines || null);
    } catch (e) {
      if (e.response?.status !== 403) toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);
  useEffect(() => {
    const onChanged = () => load();
    window.addEventListener("vacations:changed", onChanged);
    return () => window.removeEventListener("vacations:changed", onChanged);
    /* eslint-disable-next-line */
  }, [days]);

  if (loading) return null;
  if (rows.length === 0) return null;

  const fmtDe = (iso) => { const [y, m, d] = (iso || "").split("-"); return d ? `${d}.${m}.` : ""; };
  const PLANNING_BADGE = {
    ok:       { label: "Erledigt",  cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    on_track: { label: "Im Plan",   cls: "bg-slate-50 text-slate-500 border-slate-200" },
    overdue:  { label: "Überfällig", cls: "bg-red-50 text-red-700 border-red-200" },
  };

  return (
    <div className="border border-slate-200 bg-white" data-testid="quota-report">
      <div className="p-5 border-b border-slate-200 flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2">
            <CalendarRange size={11} strokeWidth={1.5} /> Kontingent-Report {year}
          </div>
          <div className="font-heading text-xl font-light tracking-tighter text-slate-900">Wer ist wann weg · Nächste {days} Tage</div>
          {deadlines && (
            <div className="text-[10px] text-slate-400 mt-1">
              Wunschurlaub-Runde: 70% bis {fmtDe(deadlines.pct70_by)} · Rest bis {fmtDe(deadlines.pct100_by)}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <select className="input-tech text-xs" value={days} onChange={(e) => setDays(parseInt(e.target.value))} data-testid="quota-horizon">
            <option value="30">30 Tage</option>
            <option value="60">60 Tage</option>
            <option value="90">90 Tage</option>
            <option value="180">180 Tage</option>
          </select>
          <button onClick={load} className="text-[10px] font-medium border border-slate-300 px-2 py-1 hover:bg-slate-100 flex items-center gap-1">
            <RefreshCcw size={11} strokeWidth={1.5} /> Aktualisieren
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="text-left p-3 font-medium">Mitarbeiter</th>
              <th className="text-left p-3 font-medium">Rolle</th>
              <th className="text-center p-3 font-medium">Kontingent</th>
              <th className="text-center p-3 font-medium">Genommen</th>
              <th className="text-center p-3 font-medium">In Prüfung</th>
              <th className="text-center p-3 font-medium">Rest</th>
              <th className="text-center p-3 font-medium">Nächste {days} T.</th>
              <th className="text-center p-3 font-medium">Wunschurlaub-Planung</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const critical = r.remaining_days <= 5;
              const b70 = PLANNING_BADGE[r.planning_status_70] || PLANNING_BADGE.on_track;
              const b100 = PLANNING_BADGE[r.planning_status_100] || PLANNING_BADGE.on_track;
              return (
                <tr key={r.user_id} className={`border-b border-slate-100 ${critical ? "bg-red-50" : ""}`} data-testid={`quota-row-${r.user_id}`}>
                  <td className="p-3 font-medium text-slate-900">{r.user_name}</td>
                  <td className="p-3 text-[10px] text-slate-600">{r.waat_role || r.role}</td>
                  <td className="p-3 text-center font-mono">{r.quota}</td>
                  <td className="p-3 text-center font-mono">{r.used_days}</td>
                  <td className="p-3 text-center font-mono text-amber-700">{r.pending_days}</td>
                  <td className={`p-3 text-center font-mono ${critical ? "text-red-700 font-bold" : ""}`}>{r.remaining_days}</td>
                  <td className="p-3 text-center font-mono">{r.upcoming_next_days > 0 ? `${r.upcoming_next_days} Tage` : "—"}</td>
                  <td className="p-3 text-center" data-testid={`quota-planning-${r.user_id}`}>
                    <div className="flex items-center justify-center gap-1">
                      <span title="70% bis 30.01." className={`text-[9px] font-bold uppercase tracking-overline px-1.5 py-0.5 border ${b70.cls}`}>70%</span>
                      <span title="Rest bis 30.05." className={`text-[9px] font-bold uppercase tracking-overline px-1.5 py-0.5 border ${b100.cls}`}>100%</span>
                    </div>
                    <div className="text-[9px] text-slate-400 mt-0.5">{r.submitted_pct}% eingereicht</div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
